import { Landmark, Plus, Search } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FirmFormDialog } from "@/features/firm-form";
import { SearchHint } from "@/features/search-hint";
import { useAuth } from "@/lib/auth";
import {
  FIRM_STATUSES, US_STATES, firmMatches, firmStatusText, firmsInState, preferredIn, stateName, stateRank,
} from "@/lib/firms";
import { useFirmSearch, useFirms } from "@/lib/queries";
import { atLeast, type Firm } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";

/**
 * Firms (ADR-045): the client's sheet as a table, with what the platform knows about each firm's
 * book. Supervisors read; admins create and edit. Nineteen rows today, so the status and state
 * filters are the page's own, narrowed here. A search is the server's (`GET /firms?q=`), because it
 * also looks in the firm fields an admin marked searchable (ADR-101); while it answers, the page
 * shows the firms it can match itself, by name, code and tag, which the answer only adds to.
 *
 * The state filter lists every state, those with firms first (ACL-538). A state's firms are shown in
 * their order there (ADR-170): by the rank an admin gives each on its Settings tab, the first marked
 * Preferred, then the unranked ones by label. Without a state the list is by label, as the server
 * sends it.
 */
export function FirmsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const firms = useFirms();
  const [search, setSearch] = React.useState("");
  const q = search.trim();
  const term = useDebounced(q, 250);
  const found = useFirmSearch(term);
  const [status, setStatus] = React.useState("all");
  const [state, setState] = React.useState("all");
  const [creating, setCreating] = React.useState(false);

  const rows = firms.data ?? [];
  const firmsPerState = new Map<string, number>();
  for (const s of rows.flatMap((f) => f.states)) firmsPerState.set(s, (firmsPerState.get(s) ?? 0) + 1);
  const withFirms = [...firmsPerState.keys()].sort();
  const withoutFirms = Object.keys(US_STATES).filter((s) => !firmsPerState.has(s)).sort();
  // The server's answer once it has one for what is typed; until then, what the page can match itself.
  const answered = q !== "" && term === q && found.data !== undefined;
  const asking = q !== "" && !answered && !found.error;
  const matched = !q ? rows : answered ? (found.data ?? []) : rows.filter((f) => firmMatches(f, q));
  const narrowed = matched.filter((f) => status === "all" || f.status === status);
  const byState = state !== "all";
  const shown = byState ? firmsInState(narrowed, state) : narrowed;
  // Preferred among all the state's firms, not just the ones a search or status leaves showing.
  const preferred = byState ? preferredIn(rows, state) : new Set<number>();
  const stateHasNoFirms = byState && !firmsPerState.has(state);
  const filtered = Boolean(q) || status !== "all" || byState;

  return (
    <Page>
      <PageHeader
        title="Firms"
        description={`${rows.length ? `${rows.length} firms · ` : ""}the clients whose leads the floor works · a lead's firm is on the lead, the calling campaign is shared`}
        action={
          canManage && (
            <Button onClick={() => setCreating(true)}>
              <Plus />
              New firm
            </Button>
          )
        }
      />
      <Card className="overflow-hidden">
        <Toolbar>
          <SearchHint entity="firm">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search firms"
              leading={<Search />}
              className="w-56"
              aria-label="Search firms"
            />
          </SearchHint>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              {FIRM_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {firmStatusText(s).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-32" aria-label="Filter by state">
              <SelectValue>{state === "all" ? "Any state" : state}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any state</SelectItem>
              {withFirms.length > 0 && (
                <>
                  <SelectGroup>
                    <SelectLabel>With firms</SelectLabel>
                    {withFirms.map((s) => (
                      <StateOption key={s} code={s} firms={firmsPerState.get(s) ?? 0} />
                    ))}
                  </SelectGroup>
                  <SelectSeparator />
                </>
              )}
              <SelectGroup>
                <SelectLabel>No firms yet</SelectLabel>
                {withoutFirms.map((s) => (
                  <StateOption key={s} code={s} firms={0} />
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {filtered && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setStatus("all"); setState("all"); }}>
              Clear
            </Button>
          )}
        </Toolbar>
        {firms.isPending ? (
          <TableSkeleton cols={8} rows={6} />
        ) : firms.error ? (
          <ErrorState
            error={firms.error}
            title="Could not load the firms"
            action={
              <Button variant="outline" onClick={() => void firms.refetch()}>
                Try again
              </Button>
            }
          />
        ) : shown.length === 0 && asking ? (
          // A word may still match a searchable field: not "no firms" until the server has said so.
          <TableSkeleton cols={8} rows={2} />
        ) : stateHasNoFirms ? (
          <EmptyState
            icon={Landmark}
            title={`No firms in ${US_STATES[state] ?? state}`}
            description={`No firm takes cases in ${stateName(state)} yet. ${canManage ? "Add the state on a firm's Settings tab" : "An admin adds a state on a firm's Settings tab"}, or pick another state.`}
            action={<Button variant="outline" onClick={() => setState("all")}>Any state</Button>}
          />
        ) : shown.length === 0 ? (
          <EmptyState
            icon={Landmark}
            title={filtered ? "No firms match" : "No firms yet"}
            description={
              filtered
                ? q ? "Try other words, or another status or state." : "Try another status or state."
                : "Seed the client's sheet (make seed-firms), or add one here."
            }
            action={canManage && !filtered ? <Button onClick={() => setCreating(true)}>New firm</Button> : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {byState && <TableHead className="w-20" title={`The firm's rank in ${stateName(state)}, 1 first`}>Priority</TableHead>}
                  <TableHead>Firm</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>States</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Contacts</TableHead>
                  <TableHead className="text-right">Working</TableHead>
                  <TableHead className="text-right">Booked</TableHead>
                  <TableHead className="text-right">Signed this month</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((firm) => (
                  <FirmLine
                    key={firm.id}
                    firm={firm}
                    rankIn={byState ? state : null}
                    preferred={preferred.has(firm.id)}
                    onOpen={() => navigate(`/firms/${firm.id}`)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <FirmFormDialog open={creating} onOpenChange={setCreating} onCreated={(firm) => navigate(`/firms/${firm.id}`)} />
    </Page>
  );
}

/** A state in the filter: its code and name, and how many firms take it. */
function StateOption({ code, firms }: { code: string; firms: number }) {
  return (
    <SelectItem value={code}>
      <span className="flex items-baseline justify-between gap-4">
        <span>
          <span className="inline-block w-7 font-mono text-xs">{code}</span>
          {US_STATES[code] ?? code}
        </span>
        {firms > 0 && <span className="text-xs tabular-nums text-text-muted">{firms}</span>}
      </span>
    </SelectItem>
  );
}

/** A firm's row. `rankIn` is the state the page is filtered to, whose Priority column the row fills. */
function FirmLine({
  firm, rankIn, preferred, onOpen,
}: {
  firm: Firm;
  rankIn: string | null;
  preferred: boolean;
  onOpen: () => void;
}) {
  const status = firmStatusText(firm.status);
  const rank = rankIn ? stateRank(firm, rankIn) : null;
  return (
    <TableRow
      interactive
      tabIndex={0}
      role="button"
      aria-label={`Open ${firm.label}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {rankIn && (
        <TableCell className="tabular-nums">{rank ?? <span className="text-text-subtle" title="Not ranked in this state">—</span>}</TableCell>
      )}
      <TableCell className="whitespace-nowrap">
        <Link to={`/firms/${firm.id}`} className="font-medium text-text hover:underline" onClick={(e) => e.stopPropagation()}>
          {firm.label}
        </Link>
        {preferred && rankIn && (
          <Badge tone="accent" size="sm" className="ml-2 align-middle" title={`The firm to send a case in ${stateName(rankIn)} to first`}>
            Preferred
          </Badge>
        )}
        {firm.name !== firm.label && <span className="block text-xs text-text-muted">{firm.name}</span>}
      </TableCell>
      <TableCell className="font-mono text-xs">{firm.code}</TableCell>
      <TableCell className="font-mono text-xs text-text-muted">{firm.tags.map((t) => t.tag).join(", ")}</TableCell>
      <TableCell className="text-xs">{firm.states.join(", ") || <span className="text-text-subtle">—</span>}</TableCell>
      <TableCell className="whitespace-nowrap">
        <Badge tone={status.tone} size="sm" title={firm.pause_reason ?? status.description}>
          {status.label}
        </Badge>
        {firm.counts.held > 0 && (
          <span className="ml-1.5 text-xs text-text-muted">{firm.counts.held.toLocaleString()} held</span>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.contacts.toLocaleString()}</TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.working.toLocaleString()}</TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.scheduled.toLocaleString()}</TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.signed_this_month.toLocaleString()}</TableCell>
    </TableRow>
  );
}
